import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Trash2, 
  Search,
  Music,
  AlertCircle,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

interface Sequence {
  id: string;
  title: string;
  artist: { name: string } | null;
  download_count: number;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  software_type: string;
}

interface CategorySequence {
  id: string;
  category_id: string;
  song_id: string;
  sort_order: number;
  sequences: Sequence;
}

interface CategorySequenceAssignmentProps {
  categoryId?: string;
}

export default function CategorySequenceAssignment({ categoryId }: CategorySequenceAssignmentProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedSequenceId, setSelectedSequenceId] = useState('');

  const queryClient = useQueryClient();

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['categories-for-assignment'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, slug, software_type')
        .order('name');

      if (error) throw error;
      return data as Category[];
    },
  });

  // Fetch sequences in selected category
  const { data: categorySequences = [], isLoading: categoryLoading } = useQuery({
    queryKey: ['category-sequences', selectedCategoryId],
    queryFn: async () => {
      if (!selectedCategoryId) return [];

      const { data, error } = await supabase
        .from('category_songs')
        .select(`
          id,
          category_id,
          song_id,
          sort_order,
          sequences:song_id (
            id,
            title,
            download_count,
            artist:artists (
              name
            )
          )
        `)
        .eq('category_id', selectedCategoryId)
        .order('sort_order');

      if (error) throw error;
      return data as CategorySequence[];
    },
    enabled: !!selectedCategoryId,
  });

  // Search sequences for assignment
  const { data: availableSequences = [], isLoading: searchLoading } = useQuery({
    queryKey: ['sequences-search', searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return [];

      const { data, error } = await supabase
        .from('sequences')
        .select(`
          id, 
          title, 
          download_count,
          artist:artists (
            name
          )
        `)
        .ilike('title', `%${searchTerm}%`)
        .limit(20);

      if (error) throw error;
      return data as Sequence[];
    },
    enabled: searchTerm.length >= 2,
  });

  // Add sequence to category mutation
  const addSequenceMutation = useMutation({
    mutationFn: async ({ categoryId, sequenceId }: { categoryId: string; sequenceId: string }) => {
      // Check if association already exists
      const { data: existing } = await supabase
        .from('category_songs')
        .select('id')
        .eq('category_id', categoryId)
        .eq('song_id', sequenceId)
        .maybeSingle();

      if (existing) {
        throw new Error('Sequence is already in this category');
      }

      // Get next sort order
      const { data: maxSort } = await supabase
        .from('category_songs')
        .select('sort_order')
        .eq('category_id', categoryId)
        .order('sort_order', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextSortOrder = (maxSort?.sort_order || 0) + 1;

      const { error } = await supabase
        .from('category_songs')
        .insert({
          category_id: categoryId,
          song_id: sequenceId,
          sort_order: nextSortOrder
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-sequences', selectedCategoryId] });
      setShowAddDialog(false);
      setSelectedSequenceId('');
      setSearchTerm('');
      toast.success('Sequence added to category successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to add sequence: ${error.message}`);
    }
  });

  // Remove sequence from category mutation
  const removeSequenceMutation = useMutation({
    mutationFn: async (categorySongId: string) => {
      const { error } = await supabase
        .from('category_songs')
        .delete()
        .eq('id', categorySongId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-sequences', selectedCategoryId] });
      toast.success('Sequence removed from category successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to remove sequence: ${error.message}`);
    }
  });

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <div className="space-y-2">
        <Label htmlFor="category-select">Select Category</Label>
        <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
          <SelectTrigger>
            <SelectValue placeholder="Choose a category to manage" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name} ({category.software_type})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCategory && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                Sequences in "{selectedCategory.name}"
              </h3>
              <p className="text-sm text-muted-foreground">
                {categorySequences.length} sequences assigned
              </p>
            </div>
            
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Sequence
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add Sequence to Category</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="sequence-search">Search Sequences</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="sequence-search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by sequence title..."
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {searchLoading && (
                    <div className="text-center py-4">Searching...</div>
                  )}

                  {availableSequences.length > 0 && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      <Label>Select a sequence to add:</Label>
                      {availableSequences.map((sequence) => (
                        <div
                          key={sequence.id}
                          className={`p-3 border rounded cursor-pointer hover:bg-accent ${
                            selectedSequenceId === sequence.id ? 'bg-accent border-primary' : ''
                          }`}
                          onClick={() => setSelectedSequenceId(sequence.id)}
                        >
                          <div className="font-medium">{sequence.title}</div>
                          {sequence.artist && (
                            <div className="text-sm text-muted-foreground">
                              by {sequence.artist.name}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground mt-1">
                            {sequence.download_count} downloads
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddDialog(false);
                        setSelectedSequenceId('');
                        setSearchTerm('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (selectedSequenceId) {
                          addSequenceMutation.mutate({
                            categoryId: selectedCategoryId,
                            sequenceId: selectedSequenceId
                          });
                        }
                      }}
                      disabled={!selectedSequenceId || addSequenceMutation.isPending}
                    >
                      {addSequenceMutation.isPending ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Add Sequence
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Sequences Table */}
          {categoryLoading ? (
            <div>Loading sequences...</div>
          ) : categorySequences.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sequence</TableHead>
                  <TableHead>Artist</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categorySequences.map((categorySequence) => (
                  <TableRow key={categorySequence.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Music className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{categorySequence.sequences.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {categorySequence.sequences.artist?.name || (
                        <span className="text-muted-foreground">Unknown</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {categorySequence.sequences.download_count} downloads
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('Remove this sequence from the category?')) {
                            removeSequenceMutation.mutate(categorySequence.id);
                          }
                        }}
                        disabled={removeSequenceMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No sequences assigned to this category yet. Click "Add Sequence" to get started.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  );
}